import { monitoringApi } from "./api";

// Milestone 5b Slice 4 (docs/operational-intelligence-milestone-5-design.md,
// "Design: Message Workflow State (5b)"): the four request/response shapes
// for the workflow-action endpoints built in 5b Slice 2
// (`weatheragent/main.py`'s `WorkflowActionPayload`/`MessageWorkflow.to_dict()`).
// Kept here rather than co-located with the frontend component that
// consumes them, matching this file's existing convention of owning the
// wire-shape types for every monitoring endpoint it wraps.
export type OperationalMessageWorkflowActionKey =
  | "acknowledge"
  | "assign"
  | "snooze"
  | "mark_investigated";

export type OperationalMessageWorkflowActionPayload = {
  action: OperationalMessageWorkflowActionKey;
  assigned_to_user_id?: string | null;
  assigned_to_group_id?: number | null;
  /** ISO-8601 timestamp, required (and must be in the future) for `snooze`. */
  snooze_until?: string | null;
  note?: string | null;
};

export type OperationalMessageWorkflowRow = {
  id: number;
  operational_message_id: number;
  action: OperationalMessageWorkflowActionKey;
  actor_user_id: string;
  assigned_to_user_id: string | null;
  assigned_to_group_id: number | null;
  snooze_until: string | null;
  note: string | null;
  created_at: string | null;
  // Only present on the POST response for `mark_investigated` (see
  // `create_operational_message_workflow_action` in weatheragent/main.py).
  related_investigation?: Record<string, unknown> | null;
};

export type OperationalMessageWorkflowState = {
  acknowledge: OperationalMessageWorkflowRow | null;
  assign: OperationalMessageWorkflowRow | null;
  snooze: OperationalMessageWorkflowRow | null;
  mark_investigated: OperationalMessageWorkflowRow | null;
};

type CacheEntry = {
  expiresAt: number;
  promise: Promise<unknown>;
};

type RequestParams = Record<string, unknown>;
type DeviceStatusRollupsResponse = {
  rollups?: Array<{ scope_type?: string | null; scope_id?: number | string | null }>;
  summary?: Record<string, unknown>;
  [key: string]: unknown;
};

const MONITORING_CACHE_TTL_MS = 10_000;
const monitoringGetCache = new Map<string, CacheEntry>();

const stableParamKey = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableParamKey).join(",")}]`;
  }
  if (value && typeof value === "object") {
    return `{${Object.entries(value as RequestParams)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entryValue]) => `${JSON.stringify(key)}:${stableParamKey(entryValue)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

const cachedMonitoringGet = async <T>(path: string, params?: RequestParams): Promise<T> => {
  const cacheKey = `${path}:${stableParamKey(params || {})}`;
  const now = Date.now();
  const cached = monitoringGetCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.promise as Promise<T>;
  }

  const promise = monitoringApi
    .get<T>(path, { params })
    .then((response) => response.data)
    .catch((error) => {
      monitoringGetCache.delete(cacheKey);
      throw error;
    });
  monitoringGetCache.set(cacheKey, {
    expiresAt: now + MONITORING_CACHE_TTL_MS,
    promise,
  });
  return promise;
};

const deviceStatusRollupParams = (params?: RequestParams): RequestParams => ({
  refresh_status: false,
  ...(params || {}),
});

const filterRollupsResponse = (
  response: DeviceStatusRollupsResponse,
  params: RequestParams
): DeviceStatusRollupsResponse => {
  const scopeType = params.scope_type == null ? null : String(params.scope_type);
  const hasScopeId = params.scope_id != null;
  const scopeId = hasScopeId ? Number(params.scope_id) : null;
  const rollups = (response.rollups || []).filter((rollup) => {
    if (scopeType && rollup.scope_type !== scopeType) return false;
    if (hasScopeId && Number(rollup.scope_id) !== scopeId) return false;
    return true;
  });

  return {
    ...response,
    scope_type: scopeType,
    scope_id: hasScopeId ? scopeId : null,
    summary: {
      ...(response.summary || {}),
      rollups_returned: rollups.length,
    },
    rollups,
  };
};

const cachedDeviceStatusRollups = async <T = unknown>(params?: RequestParams): Promise<T> => {
  const resolvedParams = deviceStatusRollupParams(params);
  const exactKey = `/devices/status-rollups:${stableParamKey(resolvedParams)}`;
  const now = Date.now();
  const exactCached = monitoringGetCache.get(exactKey);
  if (exactCached && exactCached.expiresAt > now) {
    return exactCached.promise as Promise<T>;
  }

  if (resolvedParams.scope_type != null || resolvedParams.scope_id != null) {
    const { scope_type: _scopeType, scope_id: _scopeId, ...broadParams } = resolvedParams;
    const broadKey = `/devices/status-rollups:${stableParamKey(broadParams)}`;
    let broadCached = monitoringGetCache.get(broadKey);
    if (!broadCached || broadCached.expiresAt <= now) {
      const promise = cachedMonitoringGet<DeviceStatusRollupsResponse>(
        "/devices/status-rollups",
        broadParams
      );
      // `cachedMonitoringGet` is itself `async`, so this call produces a
      // second, distinct outer promise on top of the one it stores in
      // `monitoringGetCache` under the identical `broadKey` synchronously
      // inside its own body. `broadCached` below therefore always resolves
      // from that cache lookup, not the `promise` fallback here -- this
      // `promise` reference ends up discarded, unused, and unawaited. If
      // the request rejects, that leaves an unhandled promise rejection
      // (confirmed: it crashes the Jest process, and would otherwise fire a
      // spurious "Uncaught (in promise)" in the browser) even though the
      // same rejection is already properly propagated to every real caller
      // through the promise stored in the cache below. A no-op catch here
      // marks it handled without changing any resolved value or behavior.
      promise.catch(() => {});
      broadCached = monitoringGetCache.get(broadKey) || {
        expiresAt: now + MONITORING_CACHE_TTL_MS,
        promise,
      };
    }

    const promise = broadCached.promise.then((response) =>
      filterRollupsResponse(response as DeviceStatusRollupsResponse, resolvedParams)
    );
    monitoringGetCache.set(exactKey, {
      expiresAt: broadCached.expiresAt,
      promise,
    });
    return promise as Promise<T>;
  }

  return cachedMonitoringGet<T>("/devices/status-rollups", resolvedParams);
};

export const clearMonitoringReadCache = () => {
  monitoringGetCache.clear();
};

export const monitoringReadApi = {
  getOperationalMessages: <T = unknown>(params?: RequestParams) =>
    cachedMonitoringGet<T>("/operational-messages", params),
  troubleshootOperationalMessage: <T = unknown>(messageId: number | string) =>
    monitoringApi.post<T>(`/operational-messages/${messageId}/troubleshoot`).then((response) => response.data),
  // Milestone 5b Slice 4: write path for the four workflow actions
  // (acknowledge/assign/snooze/mark_investigated). Never cached -- it's a
  // mutation, following `troubleshootOperationalMessage`'s POST convention.
  createOperationalMessageWorkflowAction: <T = OperationalMessageWorkflowRow>(
    messageId: number | string,
    payload: OperationalMessageWorkflowActionPayload
  ) =>
    monitoringApi
      .post<T>(`/operational-messages/${messageId}/workflow-actions`, payload)
      .then((response) => response.data),
  // Deliberately bypasses `cachedMonitoringGet`: callers of this read
  // (`OperationalMessageWorkflowControls`) refetch it immediately after a
  // successful `createOperationalMessageWorkflowAction` call to show the
  // just-taken action, and the 10s response cache above would otherwise
  // serve the pre-action snapshot back for that window.
  getOperationalMessageWorkflow: <T = OperationalMessageWorkflowState>(messageId: number | string) =>
    monitoringApi
      .get<T>(`/operational-messages/${messageId}/workflow`)
      .then((response) => response.data),
  getDeviceStatusRollups: <T = unknown>(params?: RequestParams) =>
    cachedDeviceStatusRollups<T>(params),
  clearCache: clearMonitoringReadCache,
};
